import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '@/api';
import { useAuth } from './useAuth';
import { getRoleRedirectPath } from '../utils/userUtils';

declare global {
  interface Window {
    google?: any;
  }
}

export interface UseGoogleAuthOptions {
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
}

export const useGoogleAuth = (options: UseGoogleAuthOptions = {}) => {
  const { updateAuth } = useAuth();
  const clientRef = useRef<any>(null);
  const codeClientRef = useRef<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  const notifyError = useCallback(
    (msg: string) => {
      if (typeof options.onError === 'function') {
        options.onError(msg);
      } else {
        // fallback simple notification

        alert(msg);
      }
    },
    [options]
  );

  const loadScript = useCallback(() => {
    if (window.google && window.google.accounts) return setIsReady(true);
    const existing = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );
    if (existing) {
      existing.addEventListener('load', () => setIsReady(true));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsReady(true);
    script.onerror = () => notifyError('Failed to load Google script');
    document.head.appendChild(script);
  }, [notifyError]);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  useEffect(() => {
    if (!isReady) return;
    const clientId = (import.meta as any).env.VITE_GOOGLE_CLIENT_ID as
      | string
      | undefined;
    if (!clientId) {
      notifyError('Missing VITE_GOOGLE_CLIENT_ID in .env.local');
      return;
    }
    clientRef.current = window.google?.accounts?.id;
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) {
      notifyError('Google OAuth is not available in this browser.');
      return;
    }
    if (!clientRef.current) {
      notifyError('Google Identity client failed to initialize.');
      return;
    }
    clientRef.current.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        const idToken: string | undefined = response?.credential;
        if (!idToken) {
          options.onError?.('No credential received from Google');
          return;
        }
        try {
          setLoading(true);
          const result = await authService.googleLogin(idToken);
          const token = (result as any)?.access_token || (result as any)?.token;
          const user = (result as any)?.user;

          if (token && user) {
            updateAuth(token, user);
          }
          options.onSuccess?.(token);
        } catch (e: any) {
          notifyError(e?.message || 'Google login failed');
        } finally {
          setLoading(false);
        }
      },
      auto_select: false,
      ux_mode: 'popup',
    });

    // OAuth 2 code flow client (popup)
    codeClientRef.current = oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: async (resp: any) => {
        // resp has { code } on success
        const code = resp?.code;
        if (!code) {
          notifyError('No authorization code returned by Google');
          return;
        }
        try {
          setLoading(true);
          const result = await authService.googleLoginCode(code);
          const token = (result as any)?.access_token || (result as any)?.token;
          const user = (result as any)?.user;

          if (token && user) {
            updateAuth(token, user);
          }
          options.onSuccess?.(token);
        } catch (e: any) {
          notifyError(e?.message || 'Google code exchange failed');
        } finally {
          setLoading(false);
        }
      },
    });
    setIsInitialized(true);
  }, [isReady, notifyError, options, updateAuth]);

  const startGoogleSignIn = useCallback(() => {
    if (codeClientRef.current) {
      try {
        codeClientRef.current.requestCode();
        return;
      } catch (e) {
        // fall through to One Tap prompt
      }
    }
    if (!clientRef.current && !codeClientRef.current) {
      notifyError('Google is not initialized yet. Try again in a moment.');
      return;
    }
    try {
      clientRef.current.prompt((notification: any) => {
        const notDisplayed = notification?.getNotDisplayedReason?.();
        const skipped = notification?.getSkippedReason?.();
        const dismissed = notification?.getDismissedReason?.();
        if (notDisplayed || skipped || dismissed) {
          console.warn('Google One Tap status', {
            notDisplayed,
            skipped,
            dismissed,
          });
          notifyError(
            'Google Sign-In was blocked or dismissed. Check cookies/popups.'
          );
        }
      });
    } catch (e) {
      notifyError('Unable to open Google Sign-In');
    }
  }, [notifyError]);

  const renderGoogleButton = useCallback(
    (element: HTMLElement | null, config?: Record<string, any>) => {
      if (!element || !window.google?.accounts?.id || !isInitialized) return;
      const defaults = {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: 260,
      } as const;
      window.google.accounts.id.renderButton(element, {
        ...defaults,
        ...(config || {}),
      });
    },
    [isInitialized]
  );

  return {
    startGoogleSignIn,
    renderGoogleButton,
    isReady,
    isInitialized,
    loading,
  };
};
